import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  Input,
  Button,
  Form,
  Table,
  Row,
  Col,
  AutoComplete,
  Switch,
  Select,
} from "antd";
import { doPatch } from "../../API/apis";
import { useStateGlobalContext } from "../../contexts/GlobalContext";
import {
  discountCalulator,
  NumberFormatter,
  validateQuantity,
} from "../../utils/validator";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Notification from "../Notification/Notification";
import showConfirmModal from "../ModalConfirm/ModalConfirm";
import InvoiceFooter from "../../pages/Dashboard/PatientSummary/InvoiceAndBilling/Invoices/component/InvoiceFooter/InvoiceFooter";
import { useSearchProduct } from "../../API/apiUtils";
import { RadioGroup } from "../GlobalComponents/GlobalComponents";

const { Option } = Select;

// Shared discount selector component
const DiscountSelector = ({ data, onChange }) => {
  return (
    <Select
      defaultValue="Select a discount"
      style={{ width: 200 }}
      onChange={onChange}
    >
      {Object.keys(data).map((key) => (
        <Option key={key} value={data[key].value}>
          <p>{data[key].name} </p>
          <p>{data[key].value}% </p>
        </Option>
      ))}
    </Select>
  );
};

// Custom hook for product checkout logic
const useProductCheckout = (officeSetting) => {
  const [edit, setEdit] = useState(false);
  const [productList, setProductList] = useState([]);
  const [editingItemKey, setEditingItemKey] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [taxApplied, setTaxApplied] = useState(officeSetting?.apply_tax);
  const [totalPriceBeforeTax, setTotalPriceBeforeTax] = useState(0);
  const [totalPriceAfterTax, setTotalPriceAfterTax] = useState(0);
  const [discount, setDiscount] = useState(0);

  const tax = officeSetting && officeSetting?.local_tax_rate;
  const taxRate = tax / 100;

  const calcTax = useCallback((invoice) => {
    const local_tax =
      taxApplied && invoice ? invoice * (1 - discount / 100) * taxRate : 0;
    return local_tax.toFixed(2);
  }, [taxApplied, discount, taxRate]);

  useEffect(() => {
    console.log('useEffect running with productList:', productList); // Debug log
    
    let subtotal = 0;
    productList.forEach((product) => {
      subtotal += product.price;
    });

    const appliedDiscount = discountApplied
      ? discountCalulator(discount, subtotal)
      : 0;
    const appliedTax = taxApplied ? calcTax(subtotal) : 0;
    const total = subtotal - appliedDiscount;
    const totalPrice = total + Number(appliedTax);
    
    console.log('Calculated totals:', { subtotal, appliedDiscount, appliedTax, totalPrice }); // Debug log
    
    setTotalPriceBeforeTax(subtotal);
    setTotalPriceAfterTax(totalPrice);
  }, [productList, discount, taxApplied, discountApplied]);

  const handleDiscountChange = (checked) => {
    if (!checked) {
      setDiscount(0);
    }
    setDiscountApplied(checked);
  };

  const handleDiscount = (value) => {
    setDiscount(parseFloat(value));
  };

  const handleTaxChange = (checked) => {
    setTaxApplied(checked);
  };

  const handleProduct = (values, options) => {
    console.log('handleProduct called with:', { values, selectedProduct, productList }); // Debug log
    
    if (selectedProduct?.quantity < values.quantity) {
      return Notification.error(
        "Quantity should not be greater than available stock!"
      );
    }
    
    const newProductList = {
      id: selectedProduct.id,
      items: selectedProduct.items,
      unit_price: parseFloat(selectedProduct?.price).toFixed(2),
      quantity: values.quantity,
      price: parseFloat(selectedProduct?.price) * values.quantity,
      instruction: values.instructions,
      key: Date.now(),
      // Add strength if available (for BuyProducts)
      ...(selectedProduct?.strength && { strength: selectedProduct.strength }),
    };
    
    console.log('New product to add:', newProductList); // Debug log
    setProductList([...productList, newProductList]);
    return true; // Return success indicator
  };

  const handleEdit = (item) => {
    setEdit(true);
    setEditingItemKey(item.key);
    return {
      product_name: item?.items,
      quantity: item?.quantity,
    };
  };

  const handleUpdate = (values) => {
    const updatedProductList = productList.map((item) =>
      item.key === editingItemKey
        ? {
            ...item,
            ...values,
            items: selectedProduct?.items,
            unit_price: selectedProduct?.price,
            price: parseFloat(selectedProduct?.price) * values?.quantity,
            // Preserve strength if it exists
            ...(item.strength && { strength: item.strength }),
          }
        : item
    );
    setProductList(updatedProductList);
    setEdit(false);
    setEditingItemKey(null);
    return true;
  };

  const handleDelete = (item) => {
    showConfirmModal({
      title: "Confirm Delete?",
      icon: null,
      content: "Are you sure you want to delete this?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      className: "custom-modal",
      onOk() {
        const updatedProductList = productList.filter(
          (product) => product.key !== item.key
        );
        setProductList(updatedProductList);
      },
    });
  };

  const updateProductQuantities = async (productList, isGuest = false) => {
    try {
      const updateRequests = productList.map((product) => {
        const ids = product.id;
        const quantities = product.quantity.toString();
        const endpoint = isGuest 
          ? `/inventory/update-guest/${ids}/?q=sell&guest=true`
          : `/inventory/update-guest/${ids}/?q=sell/`;
        
        return doPatch(endpoint, {
          quantity: [quantities],
        });
      });

      await Promise.all(updateRequests);
    } catch (error) {
      console.error("Error updating product quantities:", error);
    }
  };

  const resetForm = () => {
    setProductList([]);
    setDiscountApplied(false);
    setTaxApplied(officeSetting?.apply_tax);
    setDiscount(0);
    setEdit(false);
    setEditingItemKey(null);
  };

  return {
    // State
    edit,
    productList,
    selectedProduct,
    discountApplied,
    taxApplied,
    totalPriceBeforeTax,
    totalPriceAfterTax,
    discount,
    
    // Actions
    setSelectedProduct,
    handleDiscountChange,
    handleDiscount,
    handleTaxChange,
    handleProduct,
    handleEdit,
    handleUpdate,
    handleDelete,
    updateProductQuantities,
    resetForm,
    calcTax,
  };
};

// Main component
const ProductCheckout = ({
  visible,
  onClose,
  title,
  children,
  onCheckout,
  isGuest = false,
  showStrength = false,
  customerInfo,
  productInfo,
  saving = false,
}) => {
  const [form] = Form.useForm();
  const { officeSetting } = useStateGlobalContext();
  const { options, handleSearchProduct } = useSearchProduct();
  const currentDate = dayjs().format("MM/DD/YYYY");
  
  const {
    edit,
    productList,
    selectedProduct,
    discountApplied,
    taxApplied,
    totalPriceBeforeTax,
    totalPriceAfterTax,
    discount,
    setSelectedProduct,
    handleDiscountChange,
    handleDiscount,
    handleTaxChange,
    handleProduct,
    handleEdit,
    handleUpdate,
    handleDelete,
    updateProductQuantities,
    resetForm,
    calcTax,
  } = useProductCheckout(officeSetting);

  const onSelect = (value) => {
    const selectedMed = options.find((item) => item.value === value);
    if (selectedMed) {
      const product = {
        date: new Date(),
        id: selectedMed?.id,
        items: selectedMed?.value,
        price: parseFloat(selectedMed?.price),
        quantity: selectedMed?.quantity,
        ...(showStrength && { strength: selectedMed?.strength }),
      };
      console.log('Selected product:', product); // Debug log
      setSelectedProduct(product);
      
      // Also set the form field value for product_name
      form.setFieldValue('product_name', selectedMed?.value);
      console.log('Form field product_name set to:', selectedMed?.value);
    }
  };

  const handleFormSubmit = (values) => {
    console.log('Form submitted with values:', values); // Debug log
    console.log('Current selectedProduct:', selectedProduct); // Debug log
    console.log('Form validation passed, processing...'); // Debug log
    
    if (edit) {
      const result = handleUpdate(values);
      if (result) {
        form.setFieldsValue({
          product_name: undefined,
          quantity: undefined,
        });
      }
    } else {
      const result = handleProduct(values, options);
      if (result) {
        form.setFieldsValue({
          product_name: undefined,
          quantity: undefined,
        });
      }
    }
  };

  const handleCancel = () => {
    setTimeout(() => {
      onClose();
      form.resetFields();
      resetForm();
    }, 200);
  };

  const handleCheckout = async () => {
    if (!form.getFieldValue("payment_method")) {
      Notification.error("Please select a payment method.");
      return;
    }

    try {
      await onCheckout({
        productList,
        totalPriceBeforeTax,
        totalPriceAfterTax,
        discount,
        discountApplied,
        calcTax,
        form,
      });
      
      await updateProductQuantities(productList, isGuest);
      handleCancel();
    } catch (error) {
      Notification.error("Something went Wrong");
    }
  };

  // Dynamic columns based on showStrength
  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: () => new Date().toLocaleDateString(),
    },
    {
      title: "Items",
      dataIndex: "items",
      key: "items",
      width: showStrength ? "30%" : "40%",
      ellipsis: true,
    },
    ...(showStrength ? [{
      title: "Strength",
      dataIndex: "strength",
      key: "strength",
      width: "10%",
      ellipsis: true,
    }] : []),
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      ellipsis: true,
      render: (price) => <NumberFormatter value={price} />,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      ellipsis: true,
      align: "center ",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      ellipsis: true,
      render: (price) => <NumberFormatter value={price} />,
    },
    {
      title: "Action",
      width: "8%",
      align: "center",
      render: (text, record) => (
        <span>
          <EditOutlined
            className="mr-2 text-[#1890ff]"
            onClick={() => {
              const formValues = handleEdit(record);
              form.setFieldsValue(formValues);
            }}
          />
          <DeleteOutlined
            className="text-red-500"
            onClick={() => handleDelete(record)}
          />
        </span>
      ),
    },
  ];

  return (
    <Drawer
      title={title}
      placement="right"
      closable={true}
      onClose={handleCancel}
      open={visible}
      width={900}
      closeIcon={
        <Button size="small" className="drawer-close-icon" shape="round">
          Close
        </Button>
      }
    >
             <Form
         layout="vertical"
         form={form}
         onFinish={handleFormSubmit}
         onFinishFailed={(errorInfo) => {
           console.log('Form validation failed:', errorInfo);
         }}
         onSubmit={(e) => {
           console.log('Form onSubmit event:', e);
         }}
         onValuesChange={(changedValues, allValues) => {
           console.log('Form values changed:', { changedValues, allValues });
         }}
       >
        {/* Custom header content */}
        {children}

        {/* Product search and quantity form */}
        <Row gutter={8} className="flex items-center">
          <Col span={12}>
            <Form.Item
              label="Search Product"
              name="product_name"
              tooltip="Brand Name , Generic Name, Quantity , Price"
              rules={[
                {
                  required: true,
                  message: "Please Enter Product Name First",
                },
              ]}
            >
              <AutoComplete
                options={options}
                allowClear={true}
                onSelect={onSelect}
                onSearch={handleSearchProduct}
                placeholder="Search Products"
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Quantity"
              name="quantity"
              rules={[{ validator: validateQuantity }]}
            >
              <Input placeholder="Quantity" />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Button 
              type="primary" 
              className="mt-1" 
              htmlType="submit"
              // onClick={() => {
              //   form.submit();
              // }}
            >
              {edit ? "Update" : "Add"}
            </Button>
          </Col>
        </Row>

        {/* Product table */}
        <Table
          columns={columns}
          dataSource={productList}
          pagination={false}
          size="small"
        />

        {/* Discount and tax controls */}
        <Form.Item label="Apply Discount" name="discount">
          <Switch onChange={handleDiscountChange} />
        </Form.Item>

        {discountApplied && (
          <Form.Item label="Discounts">
            <DiscountSelector
              data={officeSetting?.discounts}
              onChange={handleDiscount}
            />
          </Form.Item>
        )}

        <Form.Item label="Apply Tax" name="tax">
          <Switch onChange={handleTaxChange} defaultChecked={taxApplied} />
        </Form.Item>

        {/* Invoice footer */}
        <div className="flex justify-end mt-2">
          <InvoiceFooter
            subTotal={totalPriceBeforeTax}
            discount={discount}
            discountAmount={discountCalulator(discount, totalPriceBeforeTax)}
            tax={calcTax(totalPriceBeforeTax)}
            grandTotal={totalPriceAfterTax}
          />
        </div>

        {/* Payment method and notes */}
        <Form.Item
          label="Select Payment Collection Method"
          name="payment_method"
        >
          <RadioGroup />
        </Form.Item>

        <Form.Item label="Notes" name="notes">
          <Input.TextArea placeholder="Free Text" showCount maxLength={300} />
        </Form.Item>

        {/* Action buttons */}
        <div className="flex justify-end">
          <Button className="mr-2" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type={productList.length === 0 ? "default" : "primary"}
            onClick={handleCheckout}
            disabled={productList.length === 0}
            loading={saving}
          >
            Buy Products
          </Button>
        </div>
      </Form>
    </Drawer>
  );
};

export default ProductCheckout;
